import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, inject, OnDestroy, OnInit, Renderer2, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed} from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentPreviewTypes, STATEMENT_VIEW_TYPES } from 'countable@helpers';
import { SPIN_APPEND, SPIN_STYLES, SpinInputModel } from 'countable@model';
import { FsExportService } from 'countable@services';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';
import { SpinDirective } from '../../../../directives/spin.directive';
import { ScheduleService } from '../../../../services/schedule.service';
import { CommonModule } from '@angular/common';
import { SharedService } from '../../../shared/shared.service';

@Component({
  selector: 'app-preview-schedule',
  imports: [CommonModule, SpinDirective],
  templateUrl: './preview-schedule.component.html',
  styleUrl: './preview-schedule.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PreviewScheduleComponent implements OnInit, OnDestroy {
  public scheduleId: WritableSignal<string> = signal<string>(null);
  public placeOfPreview: STATEMENT_VIEW_TYPES = 'Engagement';
  private destroyRef = inject(DestroyRef);
  public spinnerConfig: WritableSignal<SpinInputModel> = signal<SpinInputModel>(new SpinInputModel(SPIN_APPEND.HOST, SPIN_STYLES.BLUR_WITH_SPIN));

  @HostListener('window:storage', ['$event'])
  onStorageChange(ev: StorageEvent) {
    //   if a broadcast channel is not supported old browsers using storage event for schedules
    if(ev.key === 'schedule_preview' && ev.newValue) {
      const msg = JSON.parse(ev.newValue);
      if(msg.uuid){
        this.getParams(msg.uuid);
      }
    }
  }

  constructor(private fsService: FsExportService, private renderer: Renderer2, private el: ElementRef, private scheduleService: ScheduleService,
              private activatedRoute: ActivatedRoute, private router: Router, public toasterService: ToastrService, private sharedService: SharedService) {

  }

  ngOnInit() {
    this.scheduleService.initChannel();
    this.placeOfPreview = this.router.url.includes('trial-balance-working-sheet') ?  'Workbook' : 'Engagement';
    this.activatedRoute.queryParams.subscribe(param => {
      if(param?.NoteId){
        this.scheduleId.set(param?.NoteId ? param?.NoteId : null);
        this.getPreviewDocument();
      }
    });
    this.scheduleService.updateChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((scheduleId: string)=>{
      this.getParams(scheduleId);
    });

    this.scheduleService.message$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
        this.getParams(data.uuid);
    });
  }

  ngOnDestroy() {
    this.scheduleService.closeChannel();
  }

  getParams(uuid?: string){
    if(this.scheduleId()){
      this.placeOfPreview = this.router.url.includes('trial-balance-working-sheet') ?  'Workbook' : 'Engagement';
      if(this.placeOfPreview === 'Engagement' && uuid){
        if(uuid === this.scheduleId()){
          this.getPreviewDocument();
        }
      }else{
        this.getPreviewDocument();
      }
    }
  }

  public getPreviewDocument(): void {
    if(!this.scheduleId()) return;
    this.updateSpinnerConfig(true);
    this.updateContent('');
    this.fsService.fetchFSDocPreview(DocumentPreviewTypes.SCHEDULES, this.placeOfPreview, false, false, this.scheduleId())
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(()=> this.updateSpinnerConfig(false)) ).subscribe({
      next: res => {
        if (res) {
          this.updateContent(res);
          const element = document.getElementById('schedulesPreview');
          if (element && this.placeOfPreview === 'Engagement') {
            element.scrollIntoView({ behavior: 'instant', block: 'start' });
          }
          if(this.placeOfPreview === 'Engagement'){
            document.getElementById("schedulesPreview").style.backgroundImage = ((this.sharedService.showdraft.value && this.sharedService.showdraft.value?.isShowDraft) && this.sharedService.waterMarkUrl.getValue()) ?
              `url(${this.sharedService.waterMarkUrl.getValue()})` : "none";
          }
        }
      },
      error: e => {
        this.toasterService.error(e.error);
      }
    });
  }

  updateContent(res){
    const view = this.el.nativeElement.querySelector('div');
    this.renderer.setProperty(view, 'innerHTML', res);
  }

  private updateSpinnerConfig(value: boolean) {
    this.spinnerConfig.update((config) => ({...config, isSpinShown: value}));
  }
}
